import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { SvgNmrExportAsMatrix, SvgNmrMultipleAnalysis } from 'cheminfo-font';
import { Formik } from 'formik';
import { Filters } from 'nmr-processing';
import { useRef } from 'react';
import { IoAnalytics } from 'react-icons/io5';
import { TbBrandGoogleAnalytics } from 'react-icons/tb';
import { Button, Toolbar, TooltipHelpContent } from 'react-science/ui';
import * as yup from 'yup';
import { getMatrixFilters } from '../../../data/matrixGeneration';
import { useChartData } from '../../context/ChartContext';
import { useDispatch } from '../../context/DispatchContext';
import { usePreferences } from '../../context/PreferencesContext';
import StyledButton from '../../elements/Button';
import { GroupPane } from '../../elements/GroupPane';
import Label from '../../elements/Label';
import FormikInput from '../../elements/formik/FormikInput';
import FormikOnChange from '../../elements/formik/FormikOnChange';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import useSpectraByActiveNucleus from '../../hooks/useSpectraPerNucleus';
import { useToggleSpectraVisibility } from '../../hooks/useToggleSpectraVisibility';
import useToolsFunctions from '../../hooks/useToolsFunctions';
import { getMatrixGenerationDefaultOptions } from '../../reducer/preferences/panelsPreferencesDefaultValues';
import { options } from '../../toolbar/ToolTypes';
import { booleanToString } from '../../utility/booleanToString';
import { exportAsMatrix } from '../../utility/export';
import { tablePanelStyle } from '../extra/BasicPanelStyle';
import { PreferencesContainer } from '../extra/preferences/PreferencesContainer';
import DefaultPanelHeader from '../header/DefaultPanelHeader';
import { ExclusionsZonesTable } from './ExclusionsZonesTable';
import { FiltersOptions } from './FiltersOptions';
const { signalProcessing } = Filters;
const StickyFooter = styled.div({
    position: 'sticky',
    padding: '5px',
    bottom: 0,
    width: '100%',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row',
});
const schema = yup.object().shape({
    range: yup.object({
        from: yup.number().required(),
        to: yup.number().required(),
    }),
    numberOfPoints: yup.number().required(),
});
const labelStyle = {
    label: { width: '120px' },
    wrapper: { flex: 1, display: 'flex', flexDirection: 'row' },
};
const inputStyle = {
    input: { padding: '0.4em 0.1em', width: '100%' },
};
export const DEFAULT_MATRIX_FILTERS = getMatrixFilters();
const DEFAULT_MATRIX_OPTIONS = getMatrixGenerationDefaultOptions();
function getMatrixOptions(options, range) {
    return {
        ...DEFAULT_MATRIX_OPTIONS.matrixOptions,
        range,
        ...options,
    };
}
export const GroupPanelStyle = {
    container: { padding: '10px' },
    header: { color: 'black', fontWeight: 'bolder' },
};
export function useHasSignalProcessingFilter() {
    const spectra = useSpectraByActiveNucleus();
    if (!spectra)
        return null;
    for (const spectrum of spectra) {
        for (const filter of spectrum.filters || []) {
            if (filter.name === signalProcessing.id) {
                return filter.value;
            }
        }
    }
    return null;
}
export function MatrixGenerationPanel() {
    const { originDomain: { xDomain }, } = useChartData();
    if (xDomain[0] === undefined || xDomain[1] === undefined) {
        return null;
    }
    return _jsx(InnerMatrixGenerationPanel, {});
}
function InnerMatrixGenerationPanel() {
    const dispatch = useDispatch();
    const { dispatch: dispatchPreferences } = usePreferences();
    const { view: { spectra: { activeTab }, }, xDomain, originDomain, data, } = useChartData();
    const { getToggleVisibilityButtons } = useToggleSpectraVisibility();
    const formRef = useRef(null);
    const nucleusMatrixOptions = usePanelPreferences('matrixGeneration', activeTab);
    const spectraPreferences = usePanelPreferences('spectra', activeTab);
    const matrixOptions = getMatrixOptions(nucleusMatrixOptions.matrixOptions, {
        from: originDomain.xDomain[0],
        to: originDomain.xDomain[1],
    });
    function handleExportAsMatrix() {
        exportAsMatrix(data, spectraPreferences?.columns || [], 'Spectra Matrix');
    }
    function handleToggleStocsy() {
        dispatchPreferences({
            type: 'TOGGLE_MATRIX_GENERATION_VIEW_PROPERTY',
            payload: { key: 'showStocsy', nucleus: activeTab },
        });
    }
    function handleToggleBoxplot() {
        dispatchPreferences({
            type: 'TOGGLE_MATRIX_GENERATION_VIEW_PROPERTY',
            payload: { key: 'showBoxPlot', nucleus: activeTab },
        });
    }
    function handleSave(options) {
        dispatch({ type: 'APPLY_SIGNAL_PROCESSING_FILTER', payload: { options } });
    }
    function handleOnChange(options) {
        dispatchPreferences({
            type: 'SET_MATRIX_GENERATION_OPTIONS',
            payload: { options, nucleus: activeTab },
        });
    }
    function handleAddFilter() {
        const filters = matrixOptions.filters.slice();
        filters.push(DEFAULT_MATRIX_FILTERS[0]);
        handleOnChange({ ...matrixOptions, filters });
    }
    function handleRemoveProcessing() {
        dispatch({
            type: 'DELETE_SPECTRA_FILTER',
            payload: { filterName: signalProcessing.id },
        });
    }
    function handleUseCurrentRange() {
        handleOnChange({
            ...matrixOptions,
            range: { from: xDomain[0], to: xDomain[1] },
        });
    }
    const signalProcessingFilterData = useHasSignalProcessingFilter();
    const { showStocsy, showBoxPlot } = nucleusMatrixOptions || DEFAULT_MATRIX_OPTIONS;
    return (_jsxs("div", { css: tablePanelStyle, children: [_jsx(DefaultPanelHeader, { leftButtons: [
                    {
                        disabled: !signalProcessingFilterData,
                        icon: _jsx(SvgNmrExportAsMatrix, {}),
                        tooltip: (_jsx(TooltipHelpContent, { title: "Export spectra as a matrix", description: "Export the matrix as a tab-delimited file. The first row contains meta information labels followed by the chemical shifts. Next rows contains the meta information and the intensities of the spectra." })),
                        onClick: handleExportAsMatrix,
                    },
                    ...getToggleVisibilityButtons(),
                    {
                        disabled: !signalProcessingFilterData,
                        icon: _jsx(IoAnalytics, {}),
                        tooltip: (_jsx(TooltipHelpContent, { title: `${booleanToString(!showStocsy)} STOCSY`, subTitles: [
                                { title: 'Vertical zoom', shortcuts: ['⌥', 'Scroll wheel'] },
                                { title: 'STOCSY pivot', shortcuts: ['⇧', 'click'] },
                            ], description: "Statistical Total Correlation Spectroscopy (STOCSY) is a method to identify correlations between signals.", link: "https://doi.org/10.1021/ac048630x" })),
                        onClick: handleToggleStocsy,
                        active: showStocsy,
                    },
                    {
                        disabled: !signalProcessingFilterData,
                        icon: _jsx(TbBrandGoogleAnalytics, {}),
                        tooltip: (_jsx(TooltipHelpContent, { title: `${booleanToString(!showBoxPlot)} box plot`, subTitles: [
                                { title: 'Vertical zoom', shortcuts: ['⌥', 'Scroll wheel'] },
                            ], description: "Display box plot like information. Light grey for min/max, dark grey for 1st/3rd quartile, and black for median." })),
                        onClick: handleToggleBoxplot,
                        active: showBoxPlot,
                    },
                ] }), _jsx("div", { className: "inner-container", style: { position: 'relative' }, children: _jsxs(PreferencesContainer, { style: { backgroundColor: 'white', padding: 0 }, children: [_jsx(Formik, { innerRef: formRef, onSubmit: handleSave, initialValues: matrixOptions, enableReinitialize: true, validationSchema: schema, children: _jsxs(_Fragment, { children: [_jsx(GroupPane, { text: "Filters", style: GroupPanelStyle, renderHeader: (text) => (_jsx(FiltersPanelGroupHeader, { text: text, onAdd: handleAddFilter })), children: _jsx(FiltersOptions, {}) }), _jsx(GroupPane, { style: GroupPanelStyle, text: "Exclusions zones", renderHeader: (text) => (_jsx(CustomGroupHeader, { text: text, children: _jsx(ExclusionZonesGroupHeaderContent, {}) })), children: _jsx(ExclusionsZonesTable, {}) }), _jsxs(GroupPane, { text: "More options", style: GroupPanelStyle, renderHeader: (text) => (_jsx(CustomGroupHeader, { text: text, children: _jsx(Toolbar, { children: _jsx(Toolbar.Item, { tooltip: 'Use current range', onClick: handleUseCurrentRange, icon: _jsx(SvgNmrMultipleAnalysis, {}) }) }) })), children: [_jsxs(Label, { title: "Range", style: labelStyle, children: [_jsx(Label, { title: "From", children: _jsx(FormikInput, { name: "range.from", type: "number", style: inputStyle }) }), _jsx(Label, { title: "To", style: { container: { paddingLeft: '10px' } }, children: _jsx(FormikInput, { name: "range.to", type: "number", style: inputStyle }) })] }), _jsx(Label, { title: "Number of points", style: {
                                                    ...labelStyle,
                                                    wrapper: {},
                                                    container: { paddingTop: '10px' },
                                                }, children: _jsx(FormikInput, { name: "numberOfPoints", type: "number", style: inputStyle }) })] }), _jsx(FormikOnChange, { onChange: handleOnChange })] }) }), _jsxs(StickyFooter, { children: [_jsx(Button, { intent: "success", onClick: () => formRef.current?.submitForm(), tooltipProps: { disabled: true, content: '' }, disabled: signalProcessingFilterData &&
                                        JSON.stringify(signalProcessingFilterData) ===
                                            JSON.stringify(matrixOptions), children: signalProcessingFilterData
                                        ? 'Update processing'
                                        : 'Apply processing' }), signalProcessingFilterData && (_jsx("div", { style: { paddingLeft: '5px' }, children: _jsx(Button, { intent: "danger", onClick: handleRemoveProcessing, tooltipProps: { disabled: true, content: '' }, children: "Remove processing" }) }))] })] }) })] }));
}
function FiltersPanelGroupHeader({ text, onAdd }) {
    return (_jsxs("div", { className: "section-header", style: { display: 'flex', padding: '5px 0px' }, children: [_jsx("p", { style: { flex: 1, ...GroupPanelStyle.header }, children: text }), _jsx(StyledButton.Done, { fill: "outline", size: "xSmall", onClick: onAdd, children: "Add Filter" })] }));
}
function CustomGroupHeader({ text, children }) {
    return (_jsxs("div", { className: "section-header", style: { display: 'flex', padding: '5px 0px' }, children: [_jsx("p", { style: { flex: 1, ...GroupPanelStyle.header }, children: text }), children] }));
}
function ExclusionZonesGroupHeaderContent() {
    const { toolOptions: { selectedTool }, } = useChartData();
    const { handleChangeOption } = useToolsFunctions();
    function handleToggleExclusionZoneTool() {
        if (selectedTool !== options.matrixGenerationExclusionZones.id) {
            handleChangeOption(options.matrixGenerationExclusionZones.id);
        }
        else {
            handleChangeOption(options.zoom.id);
        }
    }
    return (_jsx(Toolbar, { children: _jsx(Toolbar.Item, { tooltip: "Select exclusions zones", onClick: handleToggleExclusionZoneTool, icon: _jsx(SvgNmrMultipleAnalysis, {}), active: selectedTool === options.matrixGenerationExclusionZones.id }) }));
}
